import { chakra, useToast } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { MutateCompanyPixelDeliveryRecipientDTO } from "@intentsify/dto";
import { WebAnalyticsAccountView } from "@intentsify/types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, Loader, Modal } from "components";
import { DateTime } from "luxon";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { DeliveryRecipientFormState, deliveryRecipientSchema } from "screens";
import { useUser } from "../../../../../../store/store.hooks";
import { DeliveryRecipientForm } from "../../../components";
import { createCompanyPixelDeliveryRecipient } from "../../../EditPixel/EditPixel.requests";

export const DeliveryRecipientModal = ({
  companyId,
  pixelId,
  isOpen,
  onClose,
}: {
  companyId: number;
  pixelId: number;
  isOpen: boolean;
  onClose: () => void;
}) => {
  const queryClient = useQueryClient();
  const toast = useToast();

  const {
    register,
    formState: { errors },
    setValue,
    getValues,
    watch,
    control,
    reset,
  } = useForm<DeliveryRecipientFormState>({
    mode: "onChange",
    resolver: zodResolver(deliveryRecipientSchema),
    defaultValues: {
      accountView: WebAnalyticsAccountView.AllAccounts,
      campaignId: null,
      startDate: DateTime.now().toISO(),
      endDate: DateTime.now().plus({ month: 1 }).toISO(),
    },
  });

  const user = useUser();

  const { mutate: createRecipient, isLoading } = useMutation({
    mutationFn: async (variables: {
      companyId: number;
      pixelId: number;
      params: MutateCompanyPixelDeliveryRecipientDTO;
    }) => {
      const { companyId, pixelId, params } = variables;
      return createCompanyPixelDeliveryRecipient(companyId, pixelId, params);
    },
    onSuccess: () => {
      toast({
        title: `Report for ${user?.email || ""} scheduled.`,
        status: "success",
      });
      queryClient.invalidateQueries({
        queryKey: ["getCompanyPixelDeliveryRecipients", pixelId],
      });
      onClose();
    },
  });

  useEffect(() => {
    setValue("email", user?.email || "");
  }, [setValue, user]);

  const onSubmit = () => {
    const data = getValues();
    createRecipient({
      companyId,
      pixelId,
      params: {
        email: data.email,
        startDate: data.startDate.slice(0, 10),
        endDate: data.endDate.slice(0, 10),
        filters: {
          accountView: data.accountView,
          campaignId: data.campaignId,
        },
      },
    });
  };

  const { email, startDate, endDate, accountView, campaignId } = watch();

  return (
    <Modal
      overflow="visible"
      title={"Schedule Automated Report"}
      isOpen={isOpen}
      onClose={() => {
        reset({ email: user?.email || "" });
        onClose();
      }}
      size="2xl"
      body={
        !isLoading ? (
          <chakra.form width="100%">
            <DeliveryRecipientForm
              companyId={companyId}
              pixelId={pixelId}
              register={register}
              errors={errors}
              recipient={{
                email,
                startDate,
                endDate,
                accountView,
                campaignId,
                campaignName: null,
              }}
              control={control}
              setValue={setValue}
            />
          </chakra.form>
        ) : (
          <Loader label="" minHeight={400} />
        )
      }
      primaryButton={
        !isLoading && (
          <Button
            size="md"
            variant="primary-teal"
            onClick={() => {
              onSubmit();
            }}
          >
            Create
          </Button>
        )
      }
    />
  );
};
